import { c as _c } from "react/compiler-runtime";
import { capitalize, Divider, Stack, Typography } from '@mui/material';
import type { ConditionOperatorEnum } from '@repo/api-gw-sdk';
import { useState } from 'react';
import { SelectedItemsBody } from '@/components/panels/selectionPanel/selectedItemsBody';
import { SelectedItemsHeader } from '@/components/panels/selectionPanel/selectedItemsHeader';
import { ScrollableWrapper } from '@/components/scroll/scrollableContext';
import { ScrollableHeader } from '@/components/scroll/scrollableHeader';
import { useScrollable } from '@/components/scroll/useScrollable';
import { SearchBar } from '@/components/search/searchBar';
import { conditionValues, type MultiSelectConditionComponentProps, PropertyPolicyRelation } from '@/types/advanceFilter';
import { pluralize } from '@/utils/string';
import { OPERATORS } from './OperatorSelect';
import { ConditionPanelTriggerButton } from './conditionComponents';
import { ConditionPanelPopover } from './conditionPanelPopover';
import { FilterOperatorSelect } from './filterOperatorSelect';
import { useConditionPanel } from './useConditionPanel';

/**
 * Editor for a property matched by typed text rather than by picking from a set
 * of known values: the operator carries the comparison and each entry adds
 * another value to match against.
 *
 * The property must declare a `policyRelation` whose operators are string
 * comparisons — `String`, `Contains`, or `SingleValueOrString` for one that also
 * offers exact-value selection — because the entry field labels itself from the
 * selected operator ("Contains...", "Starts with..."). Under `Contains` the
 * condition holds one term, so a new entry replaces the previous one.
 */
export const StringCondition = (props: MultiSelectConditionComponentProps) => {
  const [valueFilter, setValueFilter] = useState('');
  const panel = useConditionPanel(props.panelHost, () => setValueFilter(''));
  const selectedValues = conditionValues(props.condition);
  const isSingleTerm = props.property.policyRelation === PropertyPolicyRelation.Contains;
  const options = selectedValues.map(value => ({
    id: value,
    name: value
  }));
  return <>
      {!panel.isHosted && <ConditionPanelTriggerButton ref={panel.triggerRef} onClick={panel.toggle} options={options} selectedValues={selectedValues} />}
      <ConditionPanelPopover open={panel.isOpen} anchorEl={panel.anchorEl} onClose={panel.close}>
        <Stack className='w-[308px] max-h-[480px] overflow-hidden'>
          <Stack className='px-[24px] py-[12px]' sx={{}}>
            {panel.isHosted ? <Stack sx={{
            marginY: '12px'
          }}>
                <FilterOperatorSelect condition={props.condition} property={props.property} onChange={props.onChange} />
              </Stack> : <Typography className='my-[12px]' variant='body1'>
                {isSingleTerm ? `Add a ${props.property.displayName.toLowerCase()}` : `Add one or more ${pluralize(props.property.displayName.toLowerCase())}`}
              </Typography>}
            <SearchBar hideSearchIcon value={valueFilter} setValue={setValueFilter} placeholder={capitalize(`${props.property?.policyRelation && OPERATORS[props.property.policyRelation]?.[props.condition.operator as ConditionOperatorEnum]?.label}...`)} selectedValues={selectedValues} onAddClick={value_0 => {
            props.onChange({
              ...props.condition,
              value: isSingleTerm ? value_0.slice(-1) : value_0
            });
          }} showAddButton={true} />
          </Stack>
          {selectedValues.length > 0 && <>
              <Divider />
              <Stack sx={{
            overflow: 'hidden',
            flexGrow: 1
          }}>
                <ScrollableWrapper>
                  <SelectedValuesContent selectedValues={selectedValues} onClear={() => props.onChange({
                ...props.condition,
                value: []
              })} onRemove={item => {
                props.onChange({
                  ...props.condition,
                  value: selectedValues.filter(i => i !== item)
                });
              }} />
                </ScrollableWrapper>
              </Stack>
            </>}
        </Stack>
      </ConditionPanelPopover>
    </>;
};
const SelectedValuesContent = t0 => {
  const $ = _c(14);
  const {
    selectedValues,
    onClear,
    onRemove
  } = t0;
  const {
    setIsScrolledVertically
  } = useScrollable();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      "& .MuiTypography-root": {
        fontWeight: "bold"
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = selectedValues.length;
  let t3;
  if ($[1] !== selectedValues.length) {
    t3 = pluralize("value", selectedValues.length);
    $[1] = selectedValues.length;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const t4 = `${t2} ${t3} added`;
  let t5;
  if ($[3] !== onClear || $[4] !== t4) {
    t5 = <ScrollableHeader hideBorderWhenOnTop={true} className="py-[12px] px-[24px]" sx={t1}><SelectedItemsHeader title={t4} clearAll={onClear} /></ScrollableHeader>;
    $[3] = onClear;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Divider />;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== onRemove || $[8] !== selectedValues || $[9] !== setIsScrolledVertically) {
    t7 = <SelectedItemsBody items={selectedValues} renderItem={_temp} itemKey={_temp2} onRemove={onRemove} onScrollVerticallyChange={setIsScrolledVertically} />;
    $[7] = onRemove;
    $[8] = selectedValues;
    $[9] = setIsScrolledVertically;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t5 || $[12] !== t7) {
    t8 = <>{t5}{t6}{t7}</>;
    $[11] = t5;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
};
function _temp(item) {
  return item;
}
function _temp2(item_0) {
  return item_0;
}